import React from "react";
import { Formik, Form, FieldArray, Field, useFormikContext } from "formik";
import Input from "components/Input";
import ImageUpload from "components/ImageUpload";
import TextArea from "components/TextArea";
import ResetAndSubmit from "components/ResetAndSubmit";
import * as Yup from 'yup';
import { getImageValidationSchema } from "constants/validationSchemas";
import FormWrapper from "components/FormWrapper";
import Card from "components/OnboardingFormCard";
import Dropdown from "components/Dropdown";
import { generateUniqueId, DEV_BASE, API_CONSTANT_ONBOARDING } from "utils";
import axios from "axios";
import moment from "moment";
import { useGlobalState } from "state";
import { showGlobalModal } from "components/GlobalModal";
import { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-modal";
import { useNavigate } from "react-router-dom";

const AutoSave = () => {
    const { values } = useFormikContext<any>();

    useEffect(() => {
        // Check if the user has actually typed anything into ANY of the founder cards
        const hasStartedTyping = values.founders.some((f: any) =>
            f.INSTITUTE_LEADER_NAME?.trim() ||
            f.INSTITUTE_LEADER_QUALIFICATION?.trim() ||
            f.INSTITUTE_LEADER_MOBILE?.trim() ||
            f.INSTITUTE_LEADER_EMAIL?.trim()
        );

        if (hasStartedTyping) {
            // If they typed something, save the progress
            sessionStorage.setItem("founders", JSON.stringify(values.founders));
        } else {
            // If all cards are completely blank, remove it from session so progress stays at 0%
            sessionStorage.removeItem("founders");
        }

        window.dispatchEvent(new Event("formUpdated"));
    }, [values]);

    return null;
};

const otpModalStyles = {
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        transform: "translate(-50%, -50%)",
        width: "380px",
        borderRadius: "1rem",
        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        padding: "2rem 1.75rem",
        background: "#FFFFFF",
        border: "none",
    },
    overlay: {
        background: "rgba(0,0,0,0.4)",
        zIndex: 999,
    },
};

const OtpBoxes = ({
    value,
    onChange,
    disabled,
}: {
    value: string;
    onChange: (v: string) => void;
    disabled?: boolean;
}) => {
    const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
    const digits = Array(6).fill("").map((_, i) => value[i] || "");

    const handleChange = (index: number, raw: string) => {
        const digit = raw.replace(/\D/g, "").slice(-1);
        const newDigits = [...digits];
        newDigits[index] = digit;
        onChange(newDigits.join(""));
        if (digit && index < 5) inputRefs.current[index + 1]?.focus();
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Backspace") {
            if (digits[index]) {
                const newDigits = [...digits];
                newDigits[index] = "";
                onChange(newDigits.join(""));
            } else if (index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
        onChange(pasted);
        const nextIndex = Math.min(pasted.length, 5);
        inputRefs.current[nextIndex]?.focus();
    };

    return (
        <div style={{ display: "flex", gap: "8px" }}>
            {Array(6).fill(null).map((_, i) => (
                <input
                    key={i}
                    ref={(el) => { inputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digits[i]}
                    disabled={disabled}
                    onChange={(e) => handleChange(i, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(i, e)}
                    onPaste={handlePaste}
                    style={{
                        width: "44px",
                        height: "48px",
                        textAlign: "center",
                        fontSize: "1.125rem",
                        fontWeight: 600,
                        color: "#111827",
                        background: "#F3F4F6",
                        border: "1.5px solid #E5E7EB",
                        borderRadius: "8px",
                        outline: "none",
                        transition: "border-color 0.15s",
                        opacity: disabled ? 0.5 : 1,
                    }}
                    onFocus={(e) => { e.target.style.borderColor = "#3B82F6"; e.target.style.background = "#EFF6FF"; }}
                    onBlur={(e) => { e.target.style.borderColor = "#E5E7EB"; e.target.style.background = "#F3F4F6"; }}
                />
            ))}
        </div>
    );
};

const Founders = ({ handleNavigate, resetFormData, disableForm }: { handleNavigate: any, resetFormData?: any, titleAndDesNotRequired?: boolean, disableForm?: boolean }) => {
    const { setInstData } = useGlobalState();
    const navigate = useNavigate();

    const [showOtpModal, setShowOtpModal] = useState(false);
    const [emailOtp, setEmailOtp] = useState("");
    const [phoneOtp, setPhoneOtp] = useState("");
    const [otpError, setOtpError] = useState("");
    const [otpLoading, setOtpLoading] = useState(false);
    const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [resendLoading, setResendLoading] = useState(false);
    const pendingPayload = useRef<any>(null);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [resendCooldown]);

    const createEmptyFoundersData = () => ({
        ID: generateUniqueId(),
        DRAWER: false,
        INSTITUTE_LEADER_IMAGE: "",
        INSTITUTE_LEADER_NAME: "",
        GENDER: "",
        INSTITUTE_LEADER_EMAIL: "",
        COUNTRY_CODE: "+91",
        INSTITUTE_LEADER_MOBILE: "",
        INSTITUTE_LEADER_DESIGNATION: "",
        INSTITUTE_LEADER_QUALIFICATION: "",
        INSTITUTE_LEADER_EXPERIENCE: "",
        INSTITUTE_LEADER_DESCRIPTION: "",
    });

    const instituteSavedData = JSON.parse(sessionStorage.getItem("Institute") || "{}");

    const initialFoundersData = useMemo(() => {
        const savedData = JSON.parse(sessionStorage.getItem("founders") || "[]");

        return ["Founder", "Principal", "Vice Principal", "School Administrator"]
            .map((item) => (savedData?.find((f: any) => f.INSTITUTE_LEADER_DESIGNATION === item)) || ({ ...createEmptyFoundersData(), INSTITUTE_LEADER_DESIGNATION: item }))
    }, []);

    const validationSchema = Yup.object({
        founders: Yup.array().of(
            Yup.object({
                INSTITUTE_LEADER_NAME: Yup.string().trim().nullable(),

                INSTITUTE_LEADER_IMAGE: Yup.mixed().when("INSTITUTE_LEADER_NAME", (name: any, schema) =>
                    !!name?.[0] ? getImageValidationSchema("Institute leader's image is required") : schema
                ),

                COUNTRY_CODE: Yup.string().when("INSTITUTE_LEADER_NAME", (name: any, schema) =>
                    !!name?.[0] ? schema.required("Country code is required") : schema
                ),

                INSTITUTE_LEADER_EMAIL: Yup.string().when("INSTITUTE_LEADER_NAME", (name: any, schema) =>
                    !!name?.[0] ? schema.required("Email is required") : schema
                ),

                GENDER: Yup.string().when("INSTITUTE_LEADER_NAME", (name: any, schema) =>
                    !!name?.[0] ? schema.required("Gender is required") : schema
                ),

                INSTITUTE_LEADER_MOBILE: Yup.string()
                    .matches(/^\d{10}$/, "Mobile number must be exactly 10 digits")
                    .when("INSTITUTE_LEADER_NAME", (name: any, schema) =>
                        !!name?.[0] ? schema.required("Mobile number is required") : schema
                    ),

                INSTITUTE_LEADER_DESIGNATION: Yup.string().when("INSTITUTE_LEADER_NAME", (name: any, schema) =>
                    !!name?.[0] ? schema.required("Leader's designation is required") : schema
                ),

                INSTITUTE_LEADER_QUALIFICATION: Yup.string().when("INSTITUTE_LEADER_NAME", (name: any, schema) =>
                    !!name?.[0] ? schema.required("Leader's qualification is required") : schema
                ),

                INSTITUTE_LEADER_EXPERIENCE: Yup.number().when("INSTITUTE_LEADER_NAME", (name: any, schema) =>
                    !!name?.[0] ? schema.required("Leader's experience is required") : schema
                ),
            })
        ),
    });

    const institute = JSON.parse(sessionStorage.getItem("Institute") || "{}");
    const hasEmail = !!institute?.EMAIL;
    const hasPhone = !!institute?.Mobile_Number;

    const goBackToStep1 = () => {
        sessionStorage.removeItem("_INITIATE_TOKEN_");
        setShowOtpModal(false);
        navigate("/onboarding/instituteInfo");
    };

    const handleVerifyOtp = async () => {
        const sessionToken = sessionStorage.getItem("_INITIATE_TOKEN_");
        if (!sessionToken) {
            goBackToStep1();
            return;
        }

        const otpPayload: { session_token: string; email_otp?: string; phone_otp?: string } = {
            session_token: sessionToken,
        };
        if (hasEmail) otpPayload.email_otp = emailOtp;
        if (hasPhone) otpPayload.phone_otp = phoneOtp;

        setOtpLoading(true);
        setOtpError("");
        setAttemptsRemaining(null);
        try {
            const res = await axios.post(`${API_CONSTANT_ONBOARDING}/master/onboarding/verify-otp`, otpPayload);
            const verifiedToken = res?.data?.data?.verified_token ?? res?.data?.verified_token;

            const response = await axios.post(`${API_CONSTANT_ONBOARDING}/master/onboarding/process`, pendingPayload.current, {
                headers: { "x-verified-token": verifiedToken },
            });
            if (["200", "201"]?.includes(String(response?.data?.code))) {
                setInstData(response?.data?.data);
                sessionStorage.setItem("_DATA_", JSON.stringify(response?.data?.data));
                sessionStorage.removeItem("_INITIATE_TOKEN_");
                setShowOtpModal(false);
                handleNavigate();
            } else {
                setOtpError(response?.data?.message || "Something went wrong.");
            }
        } catch (err: any) {
            const detail: string = err?.response?.data?.detail || "";

            if (detail.toLowerCase().includes("too many attempts") || detail.toLowerCase().includes("please restart")) {
                goBackToStep1();
                return;
            }
            if (detail.toLowerCase().includes("session expired") || detail.toLowerCase().includes("session expired, please restart")) {
                goBackToStep1();
                return;
            }

            const match = detail.match(/(\d+)\s+attempt/i);
            if (match) {
                setAttemptsRemaining(parseInt(match[1], 10));
            }

            setOtpError(detail || "Invalid OTP. Please try again.");
        } finally {
            setOtpLoading(false);
        }
    };

    const handleResendOtp = async () => {
        const sessionToken = sessionStorage.getItem("_INITIATE_TOKEN_");
        if (!sessionToken) {
            goBackToStep1();
            return;
        }

        const type = hasEmail && hasPhone ? "both" : hasEmail ? "email" : "phone";
        setResendLoading(true);
        setOtpError("");
        setAttemptsRemaining(null);
        try {
            await axios.post(`${API_CONSTANT_ONBOARDING}/master/onboarding/resend-otp`, {
                session_token: sessionToken,
                type,
            });
            setEmailOtp("");
            setPhoneOtp("");
            setResendCooldown(30);
        } catch (err: any) {
            const detail: string = err?.response?.data?.detail || "";
            if (detail.toLowerCase().includes("session expired")) {
                goBackToStep1();
                return;
            }
            setOtpError(detail || "Failed to resend OTP. Please try again.");
        } finally {
            setResendLoading(false);
        }
    };

    return (
        <>
        <Formik
            initialValues={{ founders: initialFoundersData }}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={async (values) => {
                // Filter out empty founders (without a name)
                const filteredFounders = values.founders.filter(f => f.INSTITUTE_LEADER_NAME?.trim());

                // Save only non-empty founders
                sessionStorage.setItem("founders", JSON.stringify(filteredFounders));

                const institute = JSON.parse(sessionStorage.getItem("Institute") || "{}");

                const InstituteBranchBasicInfo = {
                    INSTITUTE_TYPE_NAME: institute?.INSTITUTE_TYPE,
                    INSTITUTE_BRANCH_NAME: institute?.NAME,
                    INSTITUTE_DOMAIN: institute?.DOMAIN_NAME,
                    INSTITUTE_ACTIVATION_DATE: moment().format("YYYY-MM-DD"),
                    InstituteBranchDetails: {
                        INSTITUTE_BRANCH_LOGO: institute?.LOGO?.selectedImage,
                        INSTITUTE_BRANCH_EMAIL_ID: institute?.EMAIL,
                    },
                    InstituteBranchLocation: {
                        INSTITUTE_BRANCH_ADDRESS: institute?.GOOGLE_MAP_POSITION?.address,
                        INSTITUTE_BRANCH_COUNTRY: institute?.GOOGLE_MAP_POSITION?.country,
                        INSTITUTE_BRANCH_STATE: institute?.GOOGLE_MAP_POSITION?.state,
                        INSTITUTE_BRANCH_CITY: institute?.GOOGLE_MAP_POSITION?.city || institute?.GOOGLE_MAP_POSITION?.state,
                        INSTITUTE_BRANCH_PINCODE: institute?.GOOGLE_MAP_POSITION?.postalCode || "",
                        INSTITUTE_BRANCH_PLACE_ID: institute?.GOOGLE_MAP_POSITION?.place_id || "",
                        INSTITUTE_BRANCH_LONGITUDE: institute?.GOOGLE_MAP_POSITION?.lng,
                        INSTITUTE_BRANCH_LATITUDE: institute?.GOOGLE_MAP_POSITION?.lat,
                    },
                    INSTITUTE_BOARD_TYPE: institute?.BOARD_TYPE,
                    INSTITUTE_FOUNDING_DATE: institute?.FOUNDING_DATE ? moment(institute.FOUNDING_DATE, ["DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601]).format("YYYY-MM-DD") : null,
                    INSTITUTE_INSTRUCTION_MEDIUM: institute?.INSTRUCTION_MEDIUM,
                    INSTITUTE_GRADE_LEVELS: institute?.GRADE_LEVELS,
                    INSTITUTE_BRANCH_ADDONS: institute?.ADDON_PROGRAMS,
                    TOTAL_STUDENTS_COUNT: +institute?.TOTAL_STUDENTS_COUNT,
                    TOTAL_FACULTY_COUNT: +institute?.TOTAL_FACULTY_COUNT,
                };

                const founders = filteredFounders.map((item) => ({
                    FounderUser: {
                        NAME: item?.INSTITUTE_LEADER_NAME,
                        GENDER: item?.GENDER,
                        COUNTRY_CODE: item?.COUNTRY_CODE,
                        MOBILE_NUMBER: item?.INSTITUTE_LEADER_MOBILE,
                        EMAIL_ID: item?.INSTITUTE_LEADER_EMAIL,
                        PHOTO_URL: item?.INSTITUTE_LEADER_IMAGE?.selectedImage,
                    },
                    DESCRIPTION: item?.INSTITUTE_LEADER_DESCRIPTION,
                    DESIGNATION: item?.INSTITUTE_LEADER_DESIGNATION,
                    QUALIFICATION: item?.INSTITUTE_LEADER_QUALIFICATION,
                    EXPERIENCE: item?.INSTITUTE_LEADER_EXPERIENCE,
                }));

                pendingPayload.current = {
                    InstituteBranchBasicInfo,
                    InstituteBranchFounderDetails: founders,
                    InstituteBranchFaculty: [],
                    InstituteBranchRecognitions: [],
                    TEMPLATE: {
                        MASTER_TEMPLATE_ID: null,
                        WEBSITE_CONTENT_FORMAT: null,
                    },
                    ChatBotConfig: {},
                    Testimonials: [],
                    Branding: [],
                    CREATION_DATETIME: moment().utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
                };

                setEmailOtp("");
                setPhoneOtp("");
                setOtpError("");
                setShowOtpModal(true);
            }}
        >
            {({ values, handleSubmit, resetForm, errors }) => (
                <FormWrapper
                    title={<></>} // Title moved inside the white card below to match design
                    childern={
                        <Form onSubmit={handleSubmit} className="w-full flex flex-col items-center  ">
                            <AutoSave />

                            {/* MAIN WHITE CARD CONTAINER */}
                            <div className="w-[100%] h-[70vh] border-[0.5px] border-gray-300 rounded-[0.73vw] bg-[#FFFFFF]  p-6 lg:p-10 mb-3 flex flex-col justify-center">

                                <h3 className="text-[2.43vh] font-inter font-[500] mb-8 text-left text-gray-900">
                                    Founders/Leader info
                                </h3>

                                <FieldArray name="founders">
                                    {({ push, remove, form }) => (
                                        <div className="grid grid-cols-4 gap-6 w-full">
                                            {values.founders.map((leaderElement, index) => (
                                                <Card
                                                    key={leaderElement?.ID}
                                                    isError={Boolean(errors?.founders?.[index])}
                                                    index={index}
                                                    handleOpenDrawer={() => form.setFieldValue(`founders.${index}.DRAWER`, true)}
                                                    handleCloseDrawer={() => form.setFieldValue(`founders.${index}.DRAWER`, false)}
                                                    onDelete={() => form.setFieldValue(`founders[${index}]`, { ...createEmptyFoundersData(), INSTITUTE_LEADER_DESIGNATION: leaderElement?.INSTITUTE_LEADER_DESIGNATION })}
                                                    element={leaderElement}

                                                    children={
                                                        <div className="flex flex-wrap gap-x-2  gap-y-4">
                                                            <div className="w-[48%]">
                                                                <Input
                                                                    disable={disableForm}
                                                                    label="Name of the founder/leader"
                                                                    name={`founders.${index}.INSTITUTE_LEADER_NAME`}
                                                                    type="text"
                                                                    placeHolder="Enter their name here"
                                                                    required
                                                                />
                                                            </div>
                                                            <div className="w-[48%]">
                                                                <Input
                                                                    disable={disableForm}
                                                                    label="Qualification"
                                                                    name={`founders.${index}.INSTITUTE_LEADER_QUALIFICATION`}
                                                                    type="text"
                                                                    placeHolder="Enter Qualification"
                                                                    required
                                                                />
                                                            </div>
                                                            <div className="w-[48%]">
                                                                <Input
                                                                    disable={disableForm}
                                                                    label="Years of Experience"
                                                                    name={`founders.${index}.INSTITUTE_LEADER_EXPERIENCE`}
                                                                    type="number"
                                                                    placeHolder="Enter years of experience"
                                                                    required
                                                                />
                                                            </div>
                                                            <div className="w-[48%]">
                                                                <Dropdown
                                                                    disable={disableForm}
                                                                    name={`founders.${index}.GENDER`}
                                                                    label="Gender"
                                                                    placeHolder="Select Gender"
                                                                    required
                                                                    options={[{ value: "MALE", label: "MALE" }, { value: "FEMALE", label: "FEMALE" }, { value: "OTHER", label: "OTHER" }]}
                                                                />
                                                            </div>
                                                            <div className="w-[48%]">
                                                                <Input
                                                                    disable={disableForm}
                                                                    label="Mobile Number"
                                                                    name={`founders.${index}.INSTITUTE_LEADER_MOBILE`}
                                                                    type="text"
                                                                    placeHolder="Enter Mobile"
                                                                    isMobile
                                                                    countryCodeName={`founders.${index}.COUNTRY_CODE`}
                                                                    required
                                                                />
                                                            </div>

                                                            <div className="w-[48%]">
                                                                <Input
                                                                    disable={disableForm}
                                                                    label="Email Address"
                                                                    name={`founders.${index}.INSTITUTE_LEADER_EMAIL`}
                                                                    type="email"
                                                                    placeHolder="Enter email"
                                                                    required
                                                                />
                                                            </div>
                                                            <div className="w-full">
                                                                <TextArea
                                                                    disabled={disableForm || !(!!leaderElement?.INSTITUTE_LEADER_NAME && !!leaderElement?.INSTITUTE_LEADER_QUALIFICATION && !!leaderElement?.INSTITUTE_LEADER_EXPERIENCE && !!leaderElement?.INSTITUTE_LEADER_DESIGNATION)}
                                                                    label="Description"
                                                                    name={`founders.${index}.INSTITUTE_LEADER_DESCRIPTION`}
                                                                    generate={{
                                                                        "category": "leaders",
                                                                        "kwargs": {
                                                                            "name": leaderElement?.INSTITUTE_LEADER_NAME,
                                                                            "org_type": instituteSavedData?.INSTITUTE_TYPE,
                                                                            "org_name": instituteSavedData?.NAME,
                                                                            "qualification": leaderElement?.INSTITUTE_LEADER_QUALIFICATION,
                                                                            "experience": leaderElement?.INSTITUTE_LEADER_EXPERIENCE,
                                                                            "designation": leaderElement?.INSTITUTE_LEADER_DESIGNATION,
                                                                        }
                                                                    }}
                                                                    autoGenerate={true}
                                                                />
                                                            </div>
                                                            <div className="w-full ">
                                                                <Field name={`founders.${index}.INSTITUTE_LEADER_IMAGE`}>
                                                                    {({ field, form, meta }: any) => (
                                                                        <ImageUpload
                                                                            isError={!!meta.touched && !!meta.error}
                                                                            disable={disableForm}
                                                                            label="Upload Profile picture"
                                                                            maxSize={5}
                                                                            labelClassNames="text-[2vh]"
                                                                            containerHeight={{
                                                                                width: "7vh",
                                                                                height: "7vh",
                                                                            }}
                                                                            imageValue={field.value}
                                                                            onChange={(item: any) => {
                                                                                form.setFieldValue(`founders.${index}.INSTITUTE_LEADER_IMAGE`, item);
                                                                            }}
                                                                        />
                                                                    )}
                                                                </Field>
                                                            </div>
                                                        </div>
                                                    }
                                                />
                                            ))}
                                        </div>
                                    )}
                                </FieldArray>
                            </div>

                            {/* BOTTOM RIGHT BUTTONS CONTAINER */}
                            <div className="w-[100%]  flex justify-end items-center gap-4 mt-6">
                                
                              

                                {!disableForm && (
                                    <div className="w-fit">
                                        <ResetAndSubmit
                                            submitToolTip={!Boolean(values?.founders?.filter(item => !!item?.INSTITUTE_LEADER_NAME)?.length) ? "At least one data entry is required for the next step." : Boolean(errors?.founders) ? "Please fill in all the required fields." : ""}
                                            isError={!Boolean(values?.founders?.filter(item => !!item?.INSTITUTE_LEADER_NAME)?.length) || Boolean(errors?.founders)}
                                            resetForm={() => {
                                                resetForm();
                                                resetFormData();
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </Form>
                    }
                />
            )}
        </Formik>

        <Modal isOpen={showOtpModal} style={otpModalStyles} onRequestClose={() => !otpLoading && !resendLoading && setShowOtpModal(false)}>
            <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
                <div style={{ textAlign: "center" }}>
                    <h2 style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 700, fontSize: "1.25rem", color: "#111827", margin: "0 0 0.5rem" }}>
                        Verify your identity
                    </h2>
                    <p style={{ fontSize: "0.8125rem", color: "#6B7280", margin: 0, lineHeight: 1.6 }}>
                        We've sent a one-time password to{" "}
                        {hasEmail && hasPhone ? (
                            <><strong style={{ color: "#111827" }}>{institute?.EMAIL}</strong>{" and "}<strong style={{ color: "#111827" }}>{institute?.Mobile_Number}</strong></>
                        ) : hasEmail ? (
                            <strong style={{ color: "#111827" }}>{institute?.EMAIL}</strong>
                        ) : (
                            <strong style={{ color: "#111827" }}>{institute?.Mobile_Number}</strong>
                        )}
                        {". "}Enter {hasEmail && hasPhone ? "both OTPs" : "the OTP"} below to continue.
                    </p>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                    {hasEmail && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            <label style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#374151" }}>Email OTP</label>
                            <OtpBoxes
                                value={emailOtp}
                                disabled={otpLoading || resendLoading}
                                onChange={(v) => { setEmailOtp(v); setOtpError(""); setAttemptsRemaining(null); }}
                            />
                        </div>
                    )}
                    {hasPhone && (
                        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            <label style={{ fontSize: "0.8125rem", fontWeight: 600, color: "#374151" }}>Phone OTP</label>
                            <OtpBoxes
                                value={phoneOtp}
                                disabled={otpLoading || resendLoading}
                                onChange={(v) => { setPhoneOtp(v); setOtpError(""); setAttemptsRemaining(null); }}
                            />
                        </div>
                    )}
                </div>

                {otpError && (
                    <div>
                        <p style={{ color: "#EF4444", fontSize: "0.8125rem", margin: 0 }}>{otpError}</p>
                        {attemptsRemaining !== null && (
                            <p style={{ color: "#F97316", fontSize: "0.75rem", margin: "0.25rem 0 0" }}>
                                {attemptsRemaining} attempt{attemptsRemaining !== 1 ? "s" : ""} remaining before the session is reset.
                            </p>
                        )}
                    </div>
                )}

                <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={otpLoading || resendLoading || (hasEmail && emailOtp.length < 6) || (hasPhone && phoneOtp.length < 6)}
                    style={{
                        width: "100%",
                        background: "#2563EB",
                        color: "#FFFFFF",
                        fontWeight: 600,
                        fontSize: "0.9375rem",
                        padding: "0.75rem",
                        borderRadius: "0.625rem",
                        border: "none",
                        cursor: (otpLoading || resendLoading || (hasEmail && emailOtp.length < 6) || (hasPhone && phoneOtp.length < 6)) ? "not-allowed" : "pointer",
                        opacity: (otpLoading || resendLoading || (hasEmail && emailOtp.length < 6) || (hasPhone && phoneOtp.length < 6)) ? 0.5 : 1,
                        transition: "background 0.15s",
                    }}
                    onMouseEnter={(e) => { if (!(e.currentTarget.disabled)) (e.currentTarget).style.background = "#1D4ED8"; }}
                    onMouseLeave={(e) => { (e.currentTarget).style.background = "#2563EB"; }}
                >
                    {otpLoading ? "Verifying…" : "Verify & Continue"}
                </button>

                <p style={{ textAlign: "center", margin: 0, fontSize: "0.8125rem", color: "#9CA3AF" }}>
                    {resendLoading ? (
                        "Resending…"
                    ) : resendCooldown > 0 ? (
                        <>Resend OTP in <strong style={{ color: "#374151" }}>{resendCooldown}s</strong></>
                    ) : (
                        <button
                            type="button"
                            onClick={handleResendOtp}
                            disabled={otpLoading}
                            style={{ background: "none", border: "none", color: "#6B7280", cursor: "pointer", padding: 0, fontSize: "0.8125rem", textDecoration: "underline" }}
                        >
                            Resend OTP
                        </button>
                    )}
                </p>
            </div>
        </Modal>
        </>
    );
};

export default Founders;